import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login, register } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../config';

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan');
  const { loginUser } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code + new password
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });
  const [resetData, setResetData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const maxRetries = 2;
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = isLogin
          ? await login({ email: formData.email, password: formData.password })
          : await register(formData);

        // If user came from pricing page with plan selection, store redirect
        if (selectedPlan) {
          localStorage.setItem('token', response.data.access_token);
          window.location.href = `/subscription?plan=${selectedPlan}`;
          return;
        }
        
        loginUser(response.data.access_token, response.data.user);
        toast.success(isLogin ? 'Zalogowano pomyślnie!' : 'Konto utworzone pomyślnie!');
        // Navigation will be handled by PublicRoute detecting logged-in user
        return;
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        // Only retry on server errors (500+), not on client errors (401, 422)
        if (status && status >= 500 && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 500));
          continue;
        }
        break;
      }
    }

    toast.error(lastError?.response?.data?.detail || 'Wystąpił błąd. Spróbuj ponownie.');
    setLoading(false);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/request-password-reset`, {
        email: resetData.email
      });
      toast.success('Kod resetujący został wysłany na email');
      // For testing - show code in toast (remove in production)
      if (response.data.debug_code) {
        toast.info(`Kod testowy: ${response.data.debug_code}`, { duration: 10000 });
      }
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wystąpił błąd');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error('Hasła nie są identyczne');
      return;
    }
    if (resetData.newPassword.length < 6) {
      toast.error('Hasło musi mieć minimum 6 znaków');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email: resetData.email,
        reset_code: resetData.code,
        new_password: resetData.newPassword
      });
      toast.success('Hasło zostało zmienione! Możesz się teraz zalogować.');
      setShowResetPassword(false);
      setResetStep(1);
      setResetData({ email: '', code: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wystąpił błąd');
    } finally {
      setLoading(false);
    }
  };

  // Password reset view
  if (showResetPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-surface dark:from-slate-900 dark:to-slate-800 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-floating p-8 border border-slate-100 dark:border-slate-700">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-white dark:bg-slate-700 shadow-md flex items-center justify-center overflow-hidden">
                <img src="/kai-logo.png" alt="KAI" className="w-16 h-16 object-contain" />
              </div>
              <h1 className="font-heading text-2xl text-primary-900 dark:text-slate-100 mb-2">
                Reset hasła
              </h1>
              <p className="text-muted-foreground dark:text-slate-400 text-sm">
                {resetStep === 1 ? 'Podaj email, aby otrzymać kod resetujący' : 'Wprowadź kod i nowe hasło'}
              </p>
            </div>

            {resetStep === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label htmlFor="reset-email" className="dark:text-slate-300">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="twoj@email.pl"
                    value={resetData.email}
                    onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
                    required
                    className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-900 text-white"
                >
                  {loading ? 'Wysyłanie...' : 'Wyślij kod resetujący'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="reset-code" className="dark:text-slate-300">Kod z emaila</Label>
                  <Input
                    id="reset-code"
                    type="text"
                    placeholder="123456"
                    value={resetData.code}
                    onChange={(e) => setResetData({ ...resetData, code: e.target.value })}
                    required
                    maxLength={6}
                    className="mt-1 dark:bg-slate-700 dark:border-slate-600 text-center text-2xl tracking-widest"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password" className="dark:text-slate-300">Nowe hasło</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={resetData.newPassword}
                    onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                    required
                    minLength={6}
                    className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="dark:text-slate-300">Potwierdź hasło</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={resetData.confirmPassword}
                    onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                    className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-900 text-white"
                >
                  {loading ? 'Zmienianie...' : 'Zmień hasło'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setResetStep(1);
                }}
                className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 font-medium"
              >
                ← Powrót do logowania
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-surface dark:from-slate-900 dark:to-slate-800 px-6">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-floating p-8 border border-slate-100 dark:border-slate-700">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-white dark:bg-slate-700 shadow-md flex items-center justify-center overflow-hidden">
              <img src="/kai-logo.png" alt="KAI" className="w-16 h-16 object-contain" />
            </div>
            <h1 className="font-heading text-3xl text-primary-900 dark:text-slate-100 mb-2">
              {isLogin ? 'Witaj ponownie' : 'Utwórz konto'}
            </h1>
            <p className="text-muted-foreground dark:text-slate-400">
              {isLogin ? 'Zaloguj się do swojego konta' : 'Załóż bezpłatne konto'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div>
                <Label htmlFor="full_name" className="dark:text-slate-300">Imię i nazwisko</Label>
                <Input
                  id="full_name"
                  data-testid="full-name-input"
                  type="text"
                  placeholder="Jan Kowalski"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required={!isLogin}
                  className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="dark:text-slate-300">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="twoj@email.pl"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1 dark:bg-slate-700 dark:border-slate-600"
              />
            </div>

            <div>
              <Label htmlFor="password" className="dark:text-slate-300">Hasło</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1 dark:bg-slate-700 dark:border-slate-600"
              />
            </div>

            <Button
              type="submit"
              data-testid={isLogin ? "login-button" : "register-button"}
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-900 text-white font-semibold py-2 rounded-lg transition-all duration-300"
            >
              {loading ? 'Proszę czekać...' : isLogin ? 'Zaloguj się' : 'Utwórz konto'}
            </Button>
          </form>

          {isLogin && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowResetPassword(true)}
                className="text-sm text-muted-foreground dark:text-slate-500 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
              >
                Zapomniałeś hasła?
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-primary-500 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 font-medium transition-colors"
            >
              {isLogin ? 'Nie masz konta? Zarejestruj się' : 'Masz już konto? Zaloguj się'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
